import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface Domain {
  id: number;
  domain: string;
  verification_status: string;
}

interface EmailTemplate {
  from: string;
  to: string;
  subject: string;
  text: string;
  html: string;
}

export const EmailSender: React.FC = () => {
  const navigate = useNavigate();
  const [domains, setDomains] = useState<Domain[]>([]);
  const [selectedDomain, setSelectedDomain] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [hasCredentials, setHasCredentials] = useState(false);
  const [quota, setQuota] = useState<any>(null);
  const [recentEmails, setRecentEmails] = useState<any[]>([]);

  // Email form fields
  const [emailForm, setEmailForm] = useState<EmailTemplate>({
    from: '',
    to: '',
    subject: '',
    text: '',
    html: ''
  });

  const [useHtml, setUseHtml] = useState(false);
  const [ccRecipients, setCcRecipients] = useState('');
  const [bccRecipients, setBccRecipients] = useState('');

  // Load verified domains
  useEffect(() => {
    fetchDomains();
    checkCredentials();
    fetchQuota();
  }, []);

  // Update from field when domain is selected
  useEffect(() => {
    if (selectedDomain) {
      setEmailForm(prev => ({
        ...prev,
        from: `noreply@${selectedDomain}`
      }));
    }
  }, [selectedDomain]);

  const fetchDomains = async () => {
    try {
      const response = await fetch('http://localhost:8787/api/domains');
      const data = await response.json();
      if (data.success && data.domains) {
        const verifiedDomains = data.domains.filter((d: Domain) =>
          d.verification_status === 'verified'
        );
        setDomains(verifiedDomains);

        // Auto-select first verified domain
        if (verifiedDomains.length > 0) {
          setSelectedDomain(verifiedDomains[0].domain);
        }
      }
    } catch (error) {
      console.error('Failed to fetch domains:', error);
    }
  };

  const checkCredentials = async () => {
    try {
      const response = await fetch('http://localhost:8787/api/aws/credentials');
      const data = await response.json();
      setHasCredentials(data.success && data.credentials && data.credentials.length > 0);
    } catch (error) {
      console.error('Failed to check credentials:', error);
      setHasCredentials(false);
    }
  };

  const fetchQuota = async () => {
    try {
      const response = await fetch('http://localhost:8787/api/email/quota');
      const data = await response.json();
      if (data.success) {
        setQuota(data.quota);
      }
    } catch (error) {
      console.error('Failed to fetch quota:', error);
    }
  };

  const handleSendEmail = async () => {
    if (!emailForm.from || !emailForm.to || !emailForm.subject) {
      setMessage({ type: 'error', text: 'Please fill in all required fields' });
      return;
    }

    if (!emailForm.text && !emailForm.html) {
      setMessage({ type: 'error', text: 'Please provide either text or HTML content' });
      return;
    }

    setIsLoading(true);
    setMessage(null);

    try {
      const emailData = {
        from: emailForm.from,
        to: emailForm.to.split(',').map(e => e.trim()),
        subject: emailForm.subject,
        text: emailForm.text || undefined,
        html: useHtml ? emailForm.html : undefined,
        cc: ccRecipients ? ccRecipients.split(',').map(e => e.trim()) : undefined,
        bcc: bccRecipients ? bccRecipients.split(',').map(e => e.trim()) : undefined,
        configurationSetName: 'email-events-config'
      };

      const response = await fetch('http://localhost:8787/api/email/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(emailData)
      });

      const data = await response.json();

      if (data.success) {
        setMessage({
          type: 'success',
          text: `Email sent successfully! Message ID: ${data.messageId}`
        });

        // Clear form after successful send
        setEmailForm({
          from: `noreply@${selectedDomain}`,
          to: '',
          subject: '',
          text: '',
          html: ''
        });
        setCcRecipients('');
        setBccRecipients('');

        // Refresh quota
        fetchQuota();

        // Add to recent emails
        setRecentEmails(prev => [{
          ...emailData,
          messageId: data.messageId,
          timestamp: new Date().toISOString()
        }, ...prev].slice(0, 5));
      } else {
        setMessage({
          type: 'error',
          text: data.error || 'Failed to send email'
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'Network error: Failed to send email'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const testTemplates = [
    {
      name: 'Welcome Email',
      subject: 'Welcome to Our Service!',
      text: 'Welcome! We\'re excited to have you on board.\n\nThis is a test welcome email.',
      html: '<h2>Welcome!</h2><p>We\'re excited to have you on board.</p><p>This is a test welcome email.</p>'
    },
    {
      name: 'Password Reset',
      subject: 'Reset Your Password',
      text: 'You requested a password reset.\n\nClick here to reset: [RESET_LINK]\n\nThis link expires in 1 hour.',
      html: '<h2>Password Reset Request</h2><p>You requested a password reset.</p><p><a href="#">Click here to reset your password</a></p><p><em>This link expires in 1 hour.</em></p>'
    },
    {
      name: 'Order Confirmation',
      subject: 'Order #12345 Confirmed',
      text: 'Thank you for your order!\n\nOrder #12345\nTotal: $99.99\n\nWe\'ll notify you when it ships.',
      html: '<h2>Order Confirmed!</h2><p>Thank you for your order!</p><ul><li>Order #12345</li><li>Total: $99.99</li></ul><p>We\'ll notify you when it ships.</p>'
    }
  ];

  const loadTemplate = (template: any) => {
    setEmailForm(prev => ({
      ...prev,
      subject: template.subject,
      text: template.text,
      html: template.html
    }));
    setUseHtml(true);
  };

  if (!hasCredentials) {
    return (
      <div className="min-h-screen bg-black text-white p-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-yellow-900/20 border border-yellow-600 rounded-lg p-6 mb-6">
            <h2 className="text-xl font-semibold mb-2 text-yellow-400">⚠️ AWS Credentials Required</h2>
            <p className="mb-4">You need to configure AWS credentials before you can send emails.</p>
            <button
              onClick={() => navigate('/iam-setup')}
              className="px-4 py-2 bg-yellow-600 hover:bg-yellow-500 rounded transition"
            >
              Configure AWS Credentials →
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (domains.length === 0) {
    return (
      <div className="min-h-screen bg-black text-white p-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-yellow-900/20 border border-yellow-600 rounded-lg p-6 mb-6">
            <h2 className="text-xl font-semibold mb-2 text-yellow-400">⚠️ No Verified Domains</h2>
            <p className="mb-4">You need at least one verified domain to send emails from.</p>
            <button
              onClick={() => navigate('/domains')}
              className="px-4 py-2 bg-yellow-600 hover:bg-yellow-500 rounded transition"
            >
              Manage Domains →
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">📧 Send Email</h1>
          <p className="text-gray-400">Compose and send emails through AWS SES</p>
        </div>

        {/* Quota Display */}
        {quota && (
          <div className="bg-gray-900 border border-gray-700 rounded-lg p-4 mb-6">
            <h3 className="font-semibold mb-2">📊 Sending Quota</h3>
            <div className="grid grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-gray-400">24hr Limit:</span>
                <span className="ml-2">{quota.max24HourSend}</span>
              </div>
              <div>
                <span className="text-gray-400">Sent Today:</span>
                <span className="ml-2">{quota.sentLast24Hours}</span>
              </div>
              <div>
                <span className="text-gray-400">Remaining:</span>
                <span className="ml-2 text-green-400">{quota.remaining}</span>
              </div>
              <div>
                <span className="text-gray-400">Rate:</span>
                <span className="ml-2">{quota.maxSendRate}/sec</span>
              </div>
            </div>
          </div>
        )}

        {/* Message Display */}
        {message && (
          <div className={`rounded-lg p-4 mb-6 ${
            message.type === 'success'
              ? 'bg-green-900/20 border border-green-600 text-green-400'
              : 'bg-red-900/20 border border-red-600 text-red-400'
          }`}>
            {message.type === 'success' ? '✅' : '❌'} {message.text}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Email Compose Form */}
          <div className="lg:col-span-2">
            <div className="bg-gray-900 border border-gray-700 rounded-lg p-6">
              <h2 className="text-xl font-semibold mb-4">Compose Email</h2>

              {/* From Field with Domain Selector */}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">From Domain</label>
                <div className="flex gap-2">
                  <select
                    value={selectedDomain}
                    onChange={(e) => setSelectedDomain(e.target.value)}
                    className="bg-black border border-gray-600 rounded px-3 py-2"
                  >
                    {domains.map(domain => (
                      <option key={domain.id} value={domain.domain}>
                        {domain.domain}
                      </option>
                    ))}
                  </select>
                  <input
                    type="email"
                    value={emailForm.from}
                    onChange={(e) => setEmailForm({...emailForm, from: e.target.value})}
                    placeholder="noreply@domain.com"
                    className="flex-1 bg-black border border-gray-600 rounded px-3 py-2"
                  />
                </div>
              </div>

              {/* To Field */}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">
                  To <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={emailForm.to}
                  onChange={(e) => setEmailForm({...emailForm, to: e.target.value})}
                  placeholder="recipient@example.com, another@example.com"
                  className="w-full bg-black border border-gray-600 rounded px-3 py-2"
                />
                <p className="text-xs text-gray-500 mt-1">Separate multiple emails with commas</p>
              </div>

              {/* CC Field */}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">CC</label>
                <input
                  type="text"
                  value={ccRecipients}
                  onChange={(e) => setCcRecipients(e.target.value)}
                  placeholder="cc@example.com"
                  className="w-full bg-black border border-gray-600 rounded px-3 py-2"
                />
              </div>

              {/* BCC Field */}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">BCC</label>
                <input
                  type="text"
                  value={bccRecipients}
                  onChange={(e) => setBccRecipients(e.target.value)}
                  placeholder="bcc@example.com"
                  className="w-full bg-black border border-gray-600 rounded px-3 py-2"
                />
              </div>

              {/* Subject */}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">
                  Subject <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={emailForm.subject}
                  onChange={(e) => setEmailForm({...emailForm, subject: e.target.value})}
                  placeholder="Your email subject"
                  className="w-full bg-black border border-gray-600 rounded px-3 py-2"
                />
              </div>

              {/* Content Type Toggle */}
              <div className="mb-4">
                <div className="flex items-center gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      checked={!useHtml}
                      onChange={() => setUseHtml(false)}
                      className="mr-2"
                    />
                    Plain Text
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      checked={useHtml}
                      onChange={() => setUseHtml(true)}
                      className="mr-2"
                    />
                    HTML
                  </label>
                </div>
              </div>

              {/* Email Body */}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">
                  {useHtml ? 'HTML Content' : 'Text Content'} <span className="text-red-400">*</span>
                </label>
                <textarea
                  value={useHtml ? emailForm.html : emailForm.text}
                  onChange={(e) => setEmailForm({
                    ...emailForm,
                    [useHtml ? 'html' : 'text']: e.target.value
                  })}
                  placeholder={useHtml ? '<h1>Hello</h1>\n<p>Your HTML content here...</p>' : 'Your email message here...'}
                  className="w-full bg-black border border-gray-600 rounded px-3 py-2 font-mono text-sm"
                  rows={10}
                />
              </div>

              {/* Send Button */}
              <button
                onClick={handleSendEmail}
                disabled={isLoading}
                className="w-full px-4 py-3 bg-blue-600 hover:bg-blue-500 disabled:bg-gray-600 rounded transition font-semibold"
              >
                {isLoading ? 'Sending...' : '🚀 Send Email'}
              </button>
            </div>
          </div>

          {/* Right Sidebar */}
          <div className="space-y-6">
            {/* Email Templates */}
            <div className="bg-gray-900 border border-gray-700 rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-4">📝 Quick Templates</h3>
              <div className="space-y-2">
                {testTemplates.map((template, idx) => (
                  <button
                    key={idx}
                    onClick={() => loadTemplate(template)}
                    className="w-full text-left px-3 py-2 bg-gray-800 hover:bg-gray-700 rounded transition"
                  >
                    {template.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Recent Emails */}
            {recentEmails.length > 0 && (
              <div className="bg-gray-900 border border-gray-700 rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-4">🕐 Recent Sends</h3>
                <div className="space-y-2 text-sm">
                  {recentEmails.map((email, idx) => (
                    <div key={idx} className="pb-2 border-b border-gray-800">
                      <div className="font-medium">{email.subject}</div>
                      <div className="text-gray-400 text-xs">
                        To: {Array.isArray(email.to) ? email.to.join(', ') : email.to}
                      </div>
                      <div className="text-gray-500 text-xs">
                        {new Date(email.timestamp).toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Tips */}
            <div className="bg-blue-900/20 border border-blue-600 rounded-lg p-4">
              <h3 className="font-semibold mb-2 text-blue-400">💡 Tips</h3>
              <ul className="text-sm space-y-1 text-gray-300">
                <li>• In sandbox mode, verify recipient emails first</li>
                <li>• Use configuration sets to track events</li>
                <li>• Check suppression list before sending</li>
                <li>• Monitor bounce and complaint rates</li>
                <li>• Test with small batches first</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="mt-8 flex gap-4">
          <button
            onClick={() => navigate('/domains')}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded transition"
          >
            ← Manage Domains
          </button>
          <button
            onClick={() => navigate('/iam-setup')}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded transition"
          >
            AWS Credentials
          </button>
        </div>
      </div>
    </div>
  );
};